import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { getJSON, patchJSON } from '@/lib/api'

interface EmailPreferenceResponse {
  email_preference: {
    promotional_email: boolean
    membership_and_billing_email: {
      required: true
      reason: string
    }
  }
}

const emailPreferenceKey = ['member-portal-email-preference'] as const

export function MemberPortalEmailPreferences() {
  const queryClient = useQueryClient()
  const preference = useQuery<EmailPreferenceResponse>({
    queryKey: emailPreferenceKey,
    queryFn: () => getJSON('/api/v1/member_portal/email_preference'),
    retry: false,
  })
  const save = useMutation({
    mutationFn: (promotionalEmail: boolean) =>
      patchJSON<EmailPreferenceResponse>('/api/v1/member_portal/email_preference', {
        email_preference: { promotional_email: promotionalEmail },
      }),
    onSuccess: (data) => queryClient.setQueryData(emailPreferenceKey, data),
  })

  if (preference.isLoading) {
    return (
      <p className="mt-4 text-body-sm text-muted-foreground" role="status">
        Loading email preferences...
      </p>
    )
  }
  if (preference.isError || !preference.data) {
    return <Alert className="mt-4">We couldn&apos;t load your email preferences.</Alert>
  }

  const data = preference.data.email_preference
  return (
    <Card className="mt-4 overflow-hidden">
      <section className="flex items-start justify-between gap-6 p-4">
        <div className="space-y-1">
          <h2 className="text-body-sm font-semibold">Promotional email</h2>
          <p className="text-caption text-muted-foreground">
            Receive newsletters, announcements, and other promotional email.
          </p>
          {save.isError && (
            <p className="text-caption text-destructive" role="alert">
              We couldn&apos;t save this preference. Please try again.
            </p>
          )}
        </div>
        <Switch
          aria-label="Promotional email"
          checked={data.promotional_email}
          disabled={save.isPending}
          onCheckedChange={(checked) => save.mutate(checked)}
        />
      </section>
      <section className="border-t border-border p-4">
        <h2 className="text-body-sm font-semibold">Membership and billing email</h2>
        <p className="mt-1 text-caption text-muted-foreground">
          {data.membership_and_billing_email.reason}
        </p>
      </section>
    </Card>
  )
}
